import classnames from 'classnames';
import { parseISO } from 'date-fns';
import {
  CSSProperties,
  HTMLAttributes,
  JSX,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Link } from 'react-router';

import DayPointer from 'components/DayPointer';
import DelegatedTooltip from 'components/DelegatedTooltip';

import { useTheme } from 'context/Theme';
import { HEX_CODE_OPACITY } from 'context/Theme/constants';

import {
  CoursePlanCardStudentCourseFragment,
  PlanningStudentCourseFragment,
} from 'graphql/types/generated';
import { Assignments } from 'graphql/types/schema';

import {
  DateRangeWithIgnoredCourses,
  formatStudentDatesTooltip,
  getProjectedStudentAssignmentDates,
} from 'utils/studentAssignment';

import styles from './CoursePlanDays.module.scss';

export enum Orientation {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export function getCoursePlanDayElId(
  courseId: string,
  day: number,
  week: number
) {
  return `course-${courseId}-week-${week}-day-${day}`;
}

/**
 * TODO: test this
 * Return a 2D array of the number of assignments assigned on each day of a course
 * @param assignments assignments in a course
 * @param numberOfWeeks duration of course in weeks
 * @param numberOfDaysPerWeek  number of days the course has each week
 */
function getAssignmentCounts(
  assignments: Pick<Assignments, 'id' | 'week' | 'day'>[],
  numberOfWeeks: number,
  numberOfDaysPerWeek: number
): number[][] {
  // initialize assignment count to 0 for all days
  const assignmentCounts: number[][] = Array.from(
    { length: numberOfWeeks },
    () => Array(numberOfDaysPerWeek).fill(0)
  );

  assignments.forEach((assignment) => {
    if (assignment.week && assignment.day) {
      assignmentCounts[assignment.week - 1][assignment.day - 1] += 1;
    }
  });

  return assignmentCounts;
}

interface CoursePlanDaysProps {
  className?: string;
  id?: string;
  assignments: Pick<Assignments, 'id' | 'week' | 'day'>[];
  numberOfWeeks: number;
  numberOfDaysPerWeek: number;
  currentWeek: number | undefined;
  currentDay: number | undefined;
  courseId: string;
  courseColor: string;
  orientation: Orientation;
  dayLink?: string;
  onClickDay?: (day: number, week: number) => void;
  studentCourses?:
    | (CoursePlanCardStudentCourseFragment | PlanningStudentCourseFragment)[]
    | null;
}

export default function CoursePlanDays({
  className,
  id,
  assignments,
  numberOfWeeks,
  numberOfDaysPerWeek,
  currentWeek,
  currentDay,
  courseId,
  courseColor,
  orientation,
  dayLink,
  onClickDay,
  studentCourses,
}: CoursePlanDaysProps): JSX.Element {
  const { theme } = useTheme();
  const weeksRef = useRef<HTMLDivElement>(null);

  const weeks = Array.from(Array(numberOfWeeks).keys());
  const days = Array.from(Array(numberOfDaysPerWeek).keys());
  const assignmentCounts = getAssignmentCounts(
    assignments,
    numberOfWeeks,
    numberOfDaysPerWeek
  );

  const studentVacations = useMemo((): DateRangeWithIgnoredCourses[][] => {
    if (!studentCourses?.length) {
      return [];
    }
    return studentCourses.map((sc) =>
      sc.student.studentEvents.map((se) => ({
        from: parseISO(se.event.start),
        to: parseISO(se.event.end),
        ignoredCourses: se.event.ignoredCourses.map((ic) => ic.course.id),
      }))
    );
  }, [studentCourses]);

  const buildTooltipContent = useCallback(
    (week: number, day: number, assignmentCount: number): JSX.Element => {
      const weekDayLabel = `Week ${week} | Day ${day}`;
      const assignmentLabel = assignmentCount
        ? `${assignmentCount} Assignment${assignmentCount !== 1 ? 's' : ''}`
        : '';

      const studentDateLines = studentCourses?.length
        ? formatStudentDatesTooltip(
            studentCourses.map((studentCourse, index) => {
              const vacations = studentVacations[index] ?? [];
              const { start: dateForDay } = getProjectedStudentAssignmentDates(
                studentCourse,
                vacations,
                day,
                week,
                0,
                courseId
              );
              return {
                studentId: studentCourse.student.id,
                studentName: studentCourse.student.name,
                date: dateForDay,
              };
            })
          )
        : [];

      return (
        <div className={styles.DayToolTip}>
          <span>{weekDayLabel}</span>
          {assignmentLabel ? <span>{assignmentLabel}</span> : null}
          {studentDateLines.length > 0 ? (
            <div className={styles.StudentDates}>
              {studentDateLines.map((line, index) => (
                <span key={index} className={styles.StudentDateEntry}>
                  {line}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      );
    },
    [studentCourses, studentVacations, courseId]
  );

  const getDayTooltipContent = useCallback(
    (element: HTMLElement) =>
      buildTooltipContent(
        Number(element.dataset.week),
        Number(element.dataset.day),
        Number(element.dataset.assignmentCount)
      ),
    [buildTooltipContent]
  );

  const getDayInlineStyles = (
    numberOfAssignments: number,
    color: string,
    isCurrentDay: boolean
  ): CSSProperties => {
    const cursor = onClickDay ? 'pointer' : undefined;
    if (isCurrentDay && numberOfAssignments > 0) {
      return {
        backgroundColor: `${theme.primary}${HEX_CODE_OPACITY[25]}`,
        border: `1px solid ${theme.primary}`,
        color: theme.primary,
        cursor,
      };
    }
    if (isCurrentDay && numberOfAssignments === 0) {
      return {
        backgroundColor: theme.contentBackground,
        border: `1px solid ${theme.primary}`,
        color: theme.primary,
        cursor,
      };
    }
    if (numberOfAssignments > 0) {
      return {
        backgroundColor: `${color}${HEX_CODE_OPACITY[25]}`,
        border: `1px solid ${color}`,
        color: color,
        cursor,
      };
    }
    return {
      backgroundColor: theme.contentBackground,
      border: `1px solid ${theme.elementBorder}`,
      color: theme.contentTertiary,
      cursor,
    };
  };

  const renderDayContainer = (
    isCurrentDay: boolean,
    assignmentCount: number,
    day: number,
    week: number
  ) => {
    const attributes: HTMLAttributes<HTMLDivElement> = {
      style: getDayInlineStyles(
        assignmentCount,
        theme.colorPalette[courseColor],
        isCurrentDay
      ),
      onClick: onClickDay ? () => onClickDay(day, week) : undefined,
    };

    const dayContainerId = getCoursePlanDayElId(courseId, day, week);

    return (
      <div
        id={dayContainerId}
        key={dayContainerId}
        className={styles.DayContainer}
        data-course-day-id={courseId}
        data-week={week}
        data-day={day}
        data-assignment-count={assignmentCount}
      >
        {isCurrentDay ? <DayPointer className={styles.DayPointer} /> : null}
        {dayLink ? (
          <Link to={dayLink}>
            <div className={styles.DayLink} {...attributes} />
          </Link>
        ) : (
          <div className={styles.DayLink} {...attributes} />
        )}
      </div>
    );
  };

  const weeksStyles = classnames(
    className,
    { [styles.WeeksHorizontal]: orientation === Orientation.Horizontal },
    { [styles.WeeksVertical]: orientation === Orientation.Vertical }
  );
  const weekStyles = classnames(
    { [styles.WeekHorizontal]: orientation === Orientation.Horizontal },
    { [styles.WeekVertical]: orientation === Orientation.Vertical }
  );

  return (
    <div id={id} ref={weeksRef} className={weeksStyles}>
      <DelegatedTooltip
        containerRef={weeksRef}
        selector="[data-course-day-id]"
        getContent={getDayTooltipContent}
      />
      {weeks.map((weekIndex: number) => {
        return (
          <div key={`plan-week-${weekIndex}`} className={weekStyles}>
            <span className={styles.WeekNumber}>{weekIndex + 1}</span>
            {days.map((dayIndex: number) => {
              const day = dayIndex + 1;
              const week = weekIndex + 1;

              const isCurrentDay = currentDay === day && currentWeek === week;

              const assignmentCount = assignmentCounts[weekIndex][dayIndex];
              return renderDayContainer(
                isCurrentDay,
                assignmentCount,
                day,
                week
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
